<template>
    <div
        v-click-outside="close"
        class="select"
        :class="{ 'is-opened': opened, 'select--small': compact, 'is-disabled': disabled }"
        :style="{ width }"
    >
        <div class="select__field">
            <button
                ref="trigger"
                type="button"
                class="select__button"
                @click="open"
                @keydown.esc="close"
                @keydown.prevent.down="highlightNext"
                @keydown.prevent.up="highlightPrev"
                @keydown.enter.prevent="selectHighlighted"
            >
                <span v-if="modelValue || valueText" class="select__value">{{ trim(valueText) }}</span>
                <span v-else class="select__placeholder">{{ placeholder }}</span>
            </button>
            <button
                v-if="clearable && modelValue"
                tabindex="-1"
                type="button"
                class="select__clear"
                title="Очистить"
                @click.stop="clear"
            >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="9" height="9">
                    <path
                        d="M505.943 6.058c-8.077-8.077-21.172-8.077-29.249 0L6.058 476.693c-8.077 8.077-8.077 21.172 0 29.249A20.612 20.612 0 0020.683 512a20.614 20.614 0 0014.625-6.059L505.943 35.306c8.076-8.076 8.076-21.171 0-29.248z"
                    />
                    <path
                        d="M505.942 476.694L35.306 6.059c-8.076-8.077-21.172-8.077-29.248 0-8.077 8.076-8.077 21.171 0 29.248l470.636 470.636a20.616 20.616 0 0014.625 6.058 20.615 20.615 0 0014.624-6.057c8.075-8.078 8.075-21.173-.001-29.25z"
                    />
                </svg>
            </button>
            <input
                :id="context.name || $attrs.name"
                :value="modelValue"
                tabindex="-1"
                type="text"
                :required="context.required || required"
                :name="context.name || $attrs.name"
                class="select__hidden-input"
            />
        </div>
        <div class="select__help">
            <slot name="help" />
        </div>
        <transition name="fade">
            <div
                v-show="opened"
                ref="dropdown"
                class="select__dropdown"
                :class="{ 'is-visible': opened }"
            >
                <div v-if="withFilter" class="select__filter-wrap">
                    <input
                        ref="filter"
                        v-model="filter"
                        :placeholder="filterPlaceholder"
                        class="select__filter"
                        type="text"
                        @keyup="onFilterKeyUp"
                    />
                    <div class="select__filter-items">
                        <span
                            v-for="(item, i) in metaTagsSelected"
                            :key="`metaTagsSelected-${i}`"
                            class="is-selected"
                            @click="deleteFilterItem(i)"
                            v-text="item"
                        />
                        <span
                            v-for="(item, i) in computedMetaTags"
                            :key="`computedMetaTags-${i}`"
                            @click="selectFilterItem(item)"
                            v-text="item"
                        />
                    </div>
                </div>

                <ul ref="list" class="select__list">
                    <slot name="list-prepend" :close="close" />
                    <slot
                        name="items"
                        :items="filteredOptions.length ? filteredOptions : options"
                        :select="select"
                    >
                        <li
                            v-for="(option, index) in filteredOptions"
                            :key="index"
                            class="select__option"
                            :class="{
                                'is-active': isOptionActive(option),
                                'is-highlighted': index === highlightedIndex,
                            }"
                            @click="select(option)"
                        >
                            <slot name="prepend" />
                            <slot :item="option" :close="close">
                                {{ getOptionLabel(option) }}
                            </slot>
                            <slot name="append" />
                        </li>
                        <li v-if="(paginate && !filter && canPaginate) || (async && canLoadMore)">
                            <div class="select__more">
                                <button class="link" @click.prevent="loadMore">Show more</button>
                            </div>
                        </li>
                    </slot>
                    <slot name="list-append" :close="close" />
                </ul>
                <div
                    v-show="emptyText && !filteredOptions.length && opened"
                    class="select__option select__option--empty"
                >
                    {{ emptyText }}
                </div>
                <slot name="content-append" :close="close" />
            </div>
        </transition>
    </div>
</template>

<script>
import i18n from '@speechki-frontend/core/i18n/i18n';
import { clickOutside } from '@speechki-frontend/shared/helpers/directives/clickOutside';
import Popper from 'popper.js';

const noop = () => {};
const empty = () => ({});

export default {
    name: 'CustomSelect',
    directives: {
        clickOutside,
    },

    inject: {
        context: {
            default: empty,
        },
        registerControl: {
            default: () => noop,
        },
    },
    emits: [
        'update:modelValue',
        'opened',
        'closed',
        'filter',
        'filterCleared',
        'loadmore',
    ],
    props: {
        modelValue: {},
        options: {
            type: Array,
            default() {
                return [];
            },
        },
        emitProp: {
            type: String,
            default: null,
        },
        viewLabel: {
            type: String,
            default: null,
        },
        placeholder: {
            type: String,
            default() {
                return i18n.global.t('misc.select');
            },
        },
        filterPlaceholder: {
            type: String,
            default() {
                return i18n.global.t('misc.search');
            },
        },
        textThreshold: {
            type: Number,
            default: 26,
        },
        width: {
            type: [String, Number],
            default: 'auto',
        },
        required: {
            type: Boolean,
            default: false,
        },
        emptyText: {
            type: String,
            default: 'No options available',
        },
        withFilter: {
            type: Boolean,
            default: false,
        },
        multipleFilter: {
            type: Boolean,
            default: false,
        },
        formValueText: {},
        filterValueText: {},
        clearable: {
            type: Boolean,
            default: false,
        },
        compact: {
            type: Boolean,
            default: false,
        },
        paginate: {
            type: Boolean,
            default: false,
        },
        paginateAmount: {
            type: Number,
            default: 100,
        },
        // returns search and loadmore events naruzhu
        async: {
            type: Boolean,
            default: false,
        },
        canLoadMore: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        popperOptions: {
            type: Object,
            default: () => {},
        },
        closeOnSelect: {
            type: Boolean,
            default: true,
        },
        metaTags: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            opened: false,
            filter: '',
            popper: undefined,
            highlightedIndex: undefined,
            page: 1,
            filterDebounceTimeout: null,
            asyncFilterTimeout: null,
            filterItems: [],
            filteredMetaTags: [],
            metaTagsSelected: [],
            filteredOptions: [],
        };
    },
    computed: {
        valueText() {
            let item = undefined;

            if (this.formValueText && typeof this.formValueText === 'string') {
                return this.formValueText;
            }

            if (this.emitProp) {
                item = this.options.find((item) => item[this.emitProp] === this.modelValue);
            } else {
                item = this.options.find((item) => item === this.modelValue);
            }

            if (item) {
                if (this.formValueText) {
                    return this.formValueText(item);
                }
                if (this.viewLabel) {
                    return item[this.viewLabel];
                }
                return item;
            }

            return '';
        },
        computedMetaTags() {
            return this.filteredMetaTags
                .map((tag) => tag.toLowerCase())
                .filter((tag) => !this.metaTagsSelected.some((t) => t.toLowerCase() === tag));
        },
        canPaginate() {
            return this.paginate && this.page * this.paginateAmount < this.options.length;
        },
    },
    watch: {
        options: {
            deep: true,
            immediate: true,
            handler(options) {
                this.filteredOptions = options;
            },
        },
        filter: {
            immediate: true,
            handler(value, oldValue) {
                let result = this.options.slice();

                this.filteredOptions = [];

                if (!value && !oldValue) {
                    this.filteredOptions = result;
                    return;
                }

                if (value && !this.async) {
                    const filterLower = this.filter.toLowerCase();

                    result = result.filter((option) => {
                        if (typeof this.filterValueText === 'function') {
                            return this.filterValueText(option).toLowerCase().includes(filterLower);
                        } else if (typeof this.formValueText === 'function') {
                            return this.formValueText(option).toLowerCase().includes(filterLower);
                        } else if (this.viewLabel) {
                            return String(option[this.viewLabel] || '')
                                .toLowerCase()
                                .includes(filterLower);
                        } else {
                            return String(option || '')
                                .toLowerCase()
                                .includes(filterLower);
                        }
                    });
                }

                if (this.metaTagsSelected.length) {
                    if (this.metaTagsSelected.length > 1 || result.length > this.options.length) {
                        result = this.options.slice();
                    }
                    result = result.filter((option) =>
                        this.metaTagsSelected.every((item) =>
                            option.meta_tags
                                .map((tag) => tag.toLowerCase())
                                .includes(item.toLowerCase())
                        )
                    );
                }

                if (this.paginate && !this.filter) {
                    result = result.slice(0, this.page * this.paginateAmount);
                }

                this.filteredOptions = result;

                if (this.async) {
                    clearTimeout(this.asyncFilterTimeout);

                    if (!value && oldValue) {
                        // Clearing search — emit immediately, no debounce needed
                        this.$emit('filter', value);
                    } else {
                        this.asyncFilterTimeout = setTimeout(() => {
                            this.$emit('filter', value);
                        }, 300);
                    }
                }
                if (!value && oldValue) {
                    this.filteredMetaTags = [];
                    this.$emit('filterCleared');
                }
            },
        },
        'selectedMetaTags.length'(length, oldLength) {
            if (!length && oldLength) this.filteredMetaTags = [];
        },
        'filteredOptions.length'() {
            if (this.withFilter && this.popper) {
                this.popper.scheduleUpdate();
            }
        },
    },
    mounted() {
        this.registerControl(this);
    },

    beforeUnmount() {
        if (this.popper) {
            this.popper.destroy();
        }
        clearTimeout(this.filterDebounceTimeout);
        this.filterDebounceTimeout = null;
        clearTimeout(this.asyncFilterTimeout);
        this.asyncFilterTimeout = null;
    },
    methods: {
        open() {
            if (this.opened) return this.close();

            this.opened = true;
            this.$emit('opened');
            this.$nextTick(() => {
                this.setupPopper();
                if (this.withFilter) {
                    this.$refs.filter.focus();
                }
                this.scrollToHighlighted();
            });
        },
        setupPopper() {
            if (this.popper === undefined) {
                this.popper = new Popper(this.$refs.trigger, this.$refs.dropdown, {
                    placement: 'bottom',
                    modifiers: {
                        offset: {
                            enabled: true,
                            offset: '0, 10',
                        },
                    },
                    ...this.popperOptions,
                });
            } else {
                this.popper.scheduleUpdate();
            }
        },
        toggle() {
            this.opened = !this.opened;
        },
        close() {
            if (!this.opened) return;

            this.opened = false;

            if (this.withFilter) {
                this.filter = '';
            }
            this.$emit('closed');
        },
        trim(text) {
            if (text.length < this.textThreshold) return text;
            return text.slice(0, this.textThreshold) + '...';
        },
        select(option) {
            if (this.emitProp) {
                this.$emit('update:modelValue', option[this.emitProp]);
            } else {
                this.$emit('update:modelValue', option);
            }
            if (this.closeOnSelect) this.close();
            this.$refs.trigger.focus();
        },
        clear() {
            this.$emit('update:modelValue', null);
        },
        isOptionActive(option) {
            if (this.emitProp) {
                return this.modelValue === option[this.emitProp];
            }
            return this.modelValue === option;
        },
        getOptionLabel(option) {
            if (this.viewLabel) {
                return option[this.viewLabel];
            }

            return option;
        },
        highlight(index) {
            this.highlightedIndex = index;

            if (this.highlightedIndex < 0) {
                this.highlightedIndex = this.filteredOptions.length - 1;
            }

            if (this.highlightedIndex > this.filteredOptions.length - 1) {
                this.highlightedIndex = 0;
            }

            this.$refs.list.children[this.highlightedIndex].scrollIntoView({ block: 'nearest' });
        },
        highlightNext() {
            if (this.highlightedIndex === undefined) {
                this.highlight(0);
                return;
            }
            this.highlight(this.highlightedIndex + 1);
        },
        highlightPrev() {
            if (this.highlightedIndex === undefined) {
                this.highlight(0);
                return;
            }
            this.highlight(this.highlightedIndex - 1);
        },
        selectByIndex() {
            this.select(this.filteredOptions[this.highlightedIndex][this.prop]);
            this.close();
        },
        scrollToHighlighted() {
            if (this.$refs.list.children.length && this.highlightedIndex) {
                this.$refs.list.children[this.highlightedIndex].scrollIntoView({
                    block: 'nearest',
                });
            }
        },
        selectHighlighted() {
            if (this.highlightedIndex === undefined) return;

            this.select(this.filteredOptions[this.highlightedIndex]);
        },
        loadMore() {
            if (this.async) {
                this.$emit('loadmore');
                return;
            }
            this.page = this.page + 1;
        },
        selectFilterItem(item) {
            this.metaTagsSelected.push(item);
            this.filteredMetaTags = [];
            this.filter = '';
            setTimeout(() => {
                this.opened = true;
            }, 0);
        },
        deleteFilterItem(idx) {
            this.metaTagsSelected.splice(idx, 1);
            setTimeout(() => {
                this.opened = true;
            }, 0);
        },
        onFilterKeyUp({ keyCode }) {
            clearTimeout(this.filterDebounceTimeout);

            this.filterDebounceTimeout = setTimeout(() => {
                if (this.multipleFilter) {
                    this.highlightedIndex = undefined;

                    if (this.metaTags.length) {
                        this.filteredMetaTags = this.metaTags.filter((tag) => {
                            const regex = new RegExp(this.filter, 'gi');
                            if (!this.filter || this.filter.length < 2) return false;
                            return tag.match(regex);
                        });
                    }
                }
            }, 250);

            if (keyCode === 27) {
                return this.filteresc();
            } else if (keyCode === 40) {
                return this.highlightNext();
            } else if (keyCode === 38) {
                return this.highlightPrev();
            } else if (keyCode === 13) {
                return this.selectHighlighted();
            }
        },
        filteresc() {
            this.filteredMetaTags = [];
            this.filter = '';
        },
    },
};
</script>

<style lang="scss" src="./CustomSelect.scss"></style>
